import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Network } from '@capacitor/network';
import { captureException } from '@sentry/capacitor';
import { ErrorWithBreadcrumb } from '~/errors';
import { Preferences } from '@capacitor/preferences';
import FileUpload, { type UploadProgress } from '~/native/FileUpload';
import type { PluginListenerHandle } from '@capacitor/core';

export const useFileUploadStore = defineStore('fileUpload', () => {
  const TIME_BETWEEN_UPLOADS = 1000;
  const MAX_UPLOAD_ATTEMPTS = 1;
  const QUEUE_STORAGE_KEY = 'file-upload-queue';
  const QUEUED_FILES_FOLDER = 'file-upload/files';
  const directory = Directory.Data;

  const isUploading = computed<boolean>(() => queue.value.some(f => f.status === 'uploading'));
  let _isUploading: boolean = false;

  const fileApi = useFileAPI();
  const settingsStore = useSettingsStore();
  const { openToast } = useUserMessages();
  const { t, locale } = useEkI18n();
  const config = useRuntimeConfig();
  const tokenStore = useTokenStore();
  const platformStore = usePlatformStore();

  Network.addListener('networkStatusChange', async () => {
    if (await isUploadPossible()) {
      queue.value.forEach((f) => {
        f.status = 'uploading';
      });
    }
  });

  async function initialize() {
    try {
      queue.value = await getAllFiles();
    } catch (error) {
      captureException(error);
    }
  }

  const queue = ref<(QAUploadMetadata | ProjectUploadMetadata)[]>([]);
  const QAQueue = computed(() => queue.value.filter(f => f.uploadType === 'qa'));
  const projectQueue = computed(() => queue.value.filter(f => f.uploadType === 'project'));
  const uploadedQueue = ref<(QAUploadMetadata | ProjectUploadMetadata)[]>([]);

  watch(queue, async (newQueue, oldQueue) => {
    saveQueueToPreferences(newQueue);

    const obsoleteFiles = oldQueue.filter(f => !newQueue.some(nf => nf.filePath === f.filePath));

    await Promise.all(obsoleteFiles.map(async (f) => {
      try {
        if (f.filePath) {
          await removeFileFromQueue(f.filePath);

          if (usePlatformStore().isWeb) {
            await Filesystem.deleteFile({ path: f.filePath, directory });
          }
        }
      } catch (error) {
        captureException(error);
      }
    }));

    setTimeout(upload, TIME_BETWEEN_UPLOADS);
  }, { deep: true });

  async function saveQueueToPreferences(queueArr: (QAUploadMetadata | ProjectUploadMetadata)[]) {
    // Remove imageData before saving to Preferences
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const queueForStorage = queueArr.map(({ imageData, ...rest }) => rest);
    await Preferences.set({ key: QUEUE_STORAGE_KEY, value: JSON.stringify(queueForStorage) });
  }

  async function getFileData(params: QAUploadMetadata | ProjectUploadMetadata | EnqueueParams): Promise<string> {
    if (params.blob === undefined && params.filePath === undefined) {
      openToast(t('fileUpload.userMessage.error'), ToastType.ERROR);
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'Unable to enqueue file. Blob and path are undefined' });
    }

    let fileData: string | undefined | Blob = undefined;

    if (params.blob && platformStore.isWeb) {
      const data = await blobToBase64(params.blob);

      if (typeof data !== 'string') {
        openToast(t('fileUpload.userMessage.error'), ToastType.ERROR);
        throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'Unable to read file data' });
      }

      fileData = data;
    }

    if (params.filePath && platformStore.isMobile) {
      const file = await Filesystem.readFile({ path: params.filePath });

      if (typeof file.data !== 'string') {
        openToast(t('fileUpload.userMessage.error'), ToastType.ERROR);
        throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'Unable to read file data' });
      }

      fileData = file.data;
    }

    if (fileData === undefined) {
      openToast(t('fileUpload.userMessage.error'), ToastType.ERROR);
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'Unable to read file data' });
    }

    return fileData;
  }

  interface enqueueOptions {
    suppressLoading?: boolean;
  }

  async function createWebFile(file: EnqueueParams) {
    const fileData: string | Blob = await getFileData(file);

    return await Filesystem.writeFile({
      path: [QUEUED_FILES_FOLDER, encodeURI(file.name)].join('/'),
      data: fileData,
      directory,
      encoding: Encoding.ASCII,
      recursive: true
    });
  }

  function sanitizeAddress(address: GeolocationDto | undefined) {
    return {
      StreetName: address?.StreetName ?? '',
      StreetNumber: address?.StreetNumber ?? '',
      Unit: address?.Unit ?? '',
      PostalCode: address?.PostalCode ?? '',
      City: address?.City ?? '',
      Country: address?.Country ?? '',
      CountryCode: address?.CountryCode ?? ''
    };
  }

  async function enqueue(params: EnqueueParams | EnqueueParams[], options: enqueueOptions = {}) {
    const {
      suppressLoading = false
    } = options;

    if (suppressLoading === false) {
      await showLoading(t('loading.uploadPrepare'));
    }

    const _params = Array.isArray(params) === true ? params : [params];

    const metadataList: (QAUploadMetadata | ProjectUploadMetadata)[] = [];

    for (const fileParams of _params) {
      const isProjectUpload = fileParams.projectId !== null && fileParams.folderId !== null;
      const isQAUpload = fileParams.qaAnswerBody !== null;

      if (isProjectUpload === false && isQAUpload === false) {
        openToast(t('fileUpload.userMessage.error'), ToastType.ERROR);
        throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'Project ID and Folder ID or QA Answer Body ID must be provided' });
      }

      const filePath = usePlatformStore().isWeb ? (await createWebFile(fileParams)).uri : fileParams.filePath;

      if (!filePath) {
        openToast(t('fileUpload.userMessage.error'), ToastType.ERROR);
        throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'File path cannot be undefined' });
      }

      const uploadMetadata: UploadMetaData = {
        filePath: filePath,
        fileName: encodeURI(fileParams.name),
        fileMimeType: fileParams.mimeType,
        attempts: 0,
        status: 'uploading',
        blob: fileParams.blob,
        exif: fileParams.exif,
        address: fileParams.address,
        comment: fileParams.comment,
        projectId: fileParams.projectId,
        worksheetId: fileParams.worksheetId
      };

      let specificUploadMetadata: QAUploadMetadata | ProjectUploadMetadata;

      if (fileParams.projectId !== null && fileParams.folderId !== null) {
        specificUploadMetadata = {
          ...uploadMetadata,
          uploadType: 'project',
          folderId: fileParams.folderId
        };
      } else if (fileParams.qaAnswerBody !== null) {
        specificUploadMetadata = {
          ...uploadMetadata,
          uploadType: 'qa',
          qaAnswerBody: fileParams.qaAnswerBody
        };
      } else {
        throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FileUpload, message: 'Project ID and Folder ID or QA Answer Body ID must be provided' });
      }

      metadataList.push(specificUploadMetadata);
    }

    await addToQueue(metadataList);

    if (suppressLoading === false) {
      hideLoading();
    }

    openToast(t('documents.userMessages.filesHasBeenEnqueued', { count: _params.length }), ToastType.INFO);
  }

  async function isUploadPossible(): Promise<boolean> {
    const { connectionType, connected } = await Network.getStatus();
    const { uploadOnCellular } = settingsStore.settings;

    return connected && (connectionType === 'wifi' || (connectionType === 'cellular' && uploadOnCellular));
  }

  const progressMap = reactive<Record<string, number | undefined>>({});

  async function upload() {
    if (_isUploading) return;
    _isUploading = true;

    let uploadProgress: PluginListenerHandle | null = null;

    try {
      const canUpload = await isUploadPossible();

      if (!canUpload) {
        queue.value.forEach((f) => {
          if (f.status === 'uploading') f.status = 'awaitingNetwork';
        });

        return;
      }

      const metadata = queue.value.find(f => f.status === 'uploading');

      if (!metadata) return;

      // Native (Phone) Upload
      if (platformStore.isMobile) {
        uploadProgress = await FileUpload.addListener('progress', (p: UploadProgress) => {
          progressMap[metadata.fileName] = p.ratio;
        });

        const { uploadUrl, projectId, folderId, qaAnswerBody } = getUploadConfig(metadata);
        const accessToken = await tokenStore.getAccessToken();
        const headers = {
          Authorization: `Bearer ${accessToken.token}`,
          localization: locale.value
        };

        const result = await FileUpload.uploadFile({
          path: metadata.filePath,
          uploadUrl,
          fileName: metadata.fileName,
          headers,
          projectId,
          qaAnswerBody,
          address: sanitizeAddress(metadata.address),
          fileFolderId: folderId,
          comment: metadata.comment,
          convertHeic: settingsStore._settings.convertHeic
        });

        if (result.success) {
          const item = queue.value.find(file => file.fileName == metadata.fileName);

          if (item) {
            item.status = 'uploaded';
            uploadedQueue.value.push(item);
          }

          queue.value = queue.value.filter(file => file.fileName !== metadata.fileName);
        } else {
          handleUploadFailure(metadata);
        }

        // Web Upload
      } else {
        const fileData: string | Blob = await getFileData(metadata);

        if (typeof fileData !== 'string') {
          throw new TypeError(`Loaded data is not a string. Data: ${fileData}`);
        }

        const formData = createFormData(fileData, metadata.fileMimeType, metadata.fileName, metadata.address, metadata.comment);
        let isUploaded = false;

        if (metadata.uploadType === 'project') {
          isUploaded = await fileApi.uploadToProject(formData, metadata.projectId, metadata.folderId, settingsStore._settings.convertHeic);
        } else if (metadata.uploadType === 'qa') {
          isUploaded = await fileApi.uploadToQA(formData, metadata.qaAnswerBody, settingsStore._settings.convertHeic);
        } else {
          throw new ErrorWithBreadcrumb({
            category: BreadCrumbCategoryEnum.FileUpload,
            message: 'Invalid upload type'
          });
        }

        if (isUploaded) {
          const item = queue.value.find(file => file.fileName == metadata.fileName);

          if (item) {
            item.status = 'uploaded';
            uploadedQueue.value.push(item);
          }

          queue.value = queue.value.filter(file => file.fileName !== metadata.fileName);
        } else {
          handleUploadFailure(metadata);
        }
      }

    } catch (error) {
      queue.value.forEach(f => f.status = 'failed');
      captureException(error);
    } finally {
      uploadProgress?.remove();
      _isUploading = false;
    }
  }

  function handleUploadFailure(metadata: QAUploadMetadata | ProjectUploadMetadata) {
    metadata.attempts += 1;
    openToast(t('fileUpload.userMessage.error', { fileName: decodeURI(metadata.fileName) }), ToastType.ERROR);

    if (metadata.attempts >= MAX_UPLOAD_ATTEMPTS) {
      metadata.status = 'failed';
      queue.value = queue.value.filter(file => file.fileName !== metadata.fileName);
    }
  }

  function getUploadConfig(metadata: QAUploadMetadata | ProjectUploadMetadata) {
    if (metadata.uploadType === 'project') {
      return {
        uploadUrl: `${config.public.apiBase}V3/File/UploadFileToProject`,
        projectId: metadata.projectId,
        folderId: metadata.folderId

      };
    }

    if (metadata.uploadType === 'qa') {
      return {
        uploadUrl: `${config.public.apiBase}V3/QA/UploadFileToQA`,
        qaAnswerBody: metadata.qaAnswerBody,
        folderId: undefined
      };
    }

    throw new ErrorWithBreadcrumb({
      category: BreadCrumbCategoryEnum.FileUpload,
      message: 'Invalid upload type'
    });
  }

  function clearFailedForProject(projectId: number) {
    queue.value = queue.value.filter(f => !(f.status === 'failed' && f.uploadType === 'project' && f.projectId === projectId));
  }

  function retry(file: UploadMetaData) {
    const metadata = queue.value.find(f => f.filePath === file.filePath);

    if (metadata) {
      metadata.status = 'uploading';
      metadata.attempts = 0;
    }
  }

  function createFormData(data: string | Blob | File, mimeType: string, name: string, address: GeolocationDto | undefined, comment: string | undefined): FormData {
    const formData = new FormData();

    if (typeof data === 'string') {
      const blob = base64ToBlob(data, mimeType);
      const file = new File([blob], name, { type: mimeType });
      formData.append('File', file);
    } else if (data instanceof File) {
      formData.append('File', data);
    } else if (data instanceof Blob) {
      const file = new File([data], name, { type: mimeType });
      formData.append('File', file);
    } else {
      throw new TypeError('Unsupported file type');
    }

    formData.append('MetadataDTO.StreetName', address?.StreetName ?? '');
    formData.append('MetadataDTO.StreetNumber', address?.StreetNumber ?? '');
    formData.append('MetadataDTO.Unit', address?.Unit ?? '');
    formData.append('MetadataDTO.PostalCode', address?.PostalCode ?? '');
    formData.append('MetadataDTO.City', address?.City ?? '');
    formData.append('MetadataDTO.Country', address?.Country ?? '');
    formData.append('MetadataDTO.CountryISO', address?.CountryCode ?? '');
    formData.append('MetadataDTO.Comment', comment ?? '');

    return formData;
  }

  // Handle local storage of the queue
  async function setImageData(file: QAUploadMetadata | ProjectUploadMetadata) {
    try {
      const fileData = await Filesystem.readFile({ path: file.filePath });
      file.imageData = base64ToDataUrl(fileData.data.toString(), file.fileMimeType);
    } catch (error) {
      captureException(error);
      file.imageData = undefined;
    }
  }

  async function addToQueue(fileMetadata: QAUploadMetadata | ProjectUploadMetadata | (QAUploadMetadata | ProjectUploadMetadata)[]) {
    const files = Array.isArray(fileMetadata) ? fileMetadata : [fileMetadata];

    await Promise.all(files.map(setImageData));

    queue.value.push(...files);
    saveQueueToPreferences(queue.value);
  }

  async function getAllFiles() {
    const storedQueue = await Preferences.get({ key: QUEUE_STORAGE_KEY });
    queue.value = storedQueue.value ? JSON.parse(storedQueue.value) : [];

    await Promise.all(queue.value.map(setImageData));

    return queue.value;
  }

  async function removeFileFromQueue(filePath: string) {
    queue.value = queue.value.filter(file => file.filePath !== filePath);
    saveQueueToPreferences(queue.value);
  }

  return {
    initialize,
    queue,
    QAQueue,
    projectQueue,
    uploadedQueue,
    enqueue,
    retry,
    clearFailedForProject,
    isUploading,
    progressMap
  };
});
